import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getExpenses, createExpense, getInventoryItems } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import ExpenseReports from '../components/ExpenseReports';
import HelpIcon from '../components/help/HelpIcon';
import './ExpensesPage.css';

function ExpensesPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('list');
  const [expenses, setExpenses] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    relatedInventoryId: '',
    isRecurring: false,
    frequency: 'MONTHLY',
  });
  const [formErrors, setFormErrors] = useState({});
  const [actionFeedback, setActionFeedback] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [expensesData, itemsData] = await Promise.all([getExpenses(), getInventoryItems()]);
      setExpenses(expensesData.expenses || []);
      setInventoryItems(itemsData.items || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.description.trim()) {
      errors.description = t('expenses.validation.descriptionRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = t('expenses.validation.amountPositive');
    }
    if (!formData.category) {
      errors.category = t('expenses.validation.categoryRequired');
    }
    if (!formData.date) {
      errors.date = t('expenses.validation.dateRequired');
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    try {
      await createExpense(formData);
      setModalOpen(false);
      setFormData({
        description: '',
        amount: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        relatedInventoryId: '',
        isRecurring: false,
        frequency: 'MONTHLY',
      });
      setActionFeedback({
        type: 'success',
        message: t('expenses.createSuccess', 'Gasto creado exitosamente'),
      });
      loadData();
    } catch (error) {
      console.error('Error creating expense:', error);
      setActionFeedback({
        type: 'error',
        message: t('expenses.createError', 'Error al crear el gasto'),
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const getFilteredExpenses = () => {
    if (activeTab === 'recurring') {
      return expenses.filter((e) => e.isRecurring);
    }
    return expenses; // List filters could be added here
  };

  if (loading) return <div className="loading">{t('common.loading')}</div>;

  return (
    <div className="expenses-page">
      {actionFeedback && (
        <div
          className={`expenses-feedback expenses-feedback--${actionFeedback.type}`}
          role={actionFeedback.type === 'error' ? 'alert' : 'status'}
          aria-live={actionFeedback.type === 'error' ? 'assertive' : 'polite'}
        >
          <span className="material-icons">
            {actionFeedback.type === 'error' ? 'error' : 'check_circle'}
          </span>
          {actionFeedback.message}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setActionFeedback(null)}
            aria-label={t('common.close', 'Cerrar')}
          >
            <span className="material-icons">close</span>
          </button>
        </div>
      )}
      <div className="page-header">
        <h1>
          {t('expenses.title')} <HelpIcon topicKey="expenses.overview" />
        </h1>
        <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
          {t('expenses.addExpense')}
        </button>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          {t('expenses.list')}
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'recurring' ? 'active' : ''}`}
          onClick={() => setActiveTab('recurring')}
        >
          {t('expenses.recurring')}
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          {t('expenses.reports')}
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'reports' ? (
          <ExpenseReports expenses={expenses} />
        ) : (
          <div className="table-responsive">
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('common.description')}</th>
                  <th>
                    {t('common.category')} <HelpIcon topicKey="expenses.category" />
                  </th>
                  <th>{t('common.amount')}</th>
                  <th>{t('expenses.frequency')}</th>
                  <th>{t('expenses.relatedItem')}</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredExpenses().length > 0 ? (
                  getFilteredExpenses().map((expense) => (
                    <tr key={expense.expenseId}>
                      <td>{new Date(expense.date).toLocaleDateString()}</td>
                      <td>{expense.description}</td>
                      <td>
                        <span className="badge badge-info">{expense.category}</span>
                      </td>
                      <td className="amount-cell">{formatCurrency(parseFloat(expense.amount))}</td>
                      <td>
                        {expense.isRecurring ? (
                          <span className="badge badge-warning">{expense.frequency}</span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {expense.relatedInventoryId
                          ? inventoryItems.find((i) => i.itemId === expense.relatedInventoryId)
                              ?.name || '...'
                          : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{t('expenses.newExpense')}</h3>
            <form onSubmit={handleSubmit} noValidate>
              <div className={`form-group ${formErrors.description ? 'has-error' : ''}`}>
                <label htmlFor="expense-description">{t('common.description')}</label>
                <input
                  id="expense-description"
                  type="text"
                  name="description"
                  className={formErrors.description ? 'input-error' : ''}
                  value={formData.description}
                  onChange={handleInputChange}
                  aria-invalid={!!formErrors.description}
                  aria-describedby={
                    formErrors.description ? 'expense-description-error' : undefined
                  }
                />
                {formErrors.description && (
                  <span id="expense-description-error" className="field-error" role="alert">
                    {formErrors.description}
                  </span>
                )}
              </div>

              <div className="form-row">
                <div className={`form-group ${formErrors.amount ? 'has-error' : ''}`}>
                  <label htmlFor="expense-amount">{t('common.amount')}</label>
                  <input
                    id="expense-amount"
                    type="number"
                    name="amount"
                    className={formErrors.amount ? 'input-error' : ''}
                    value={formData.amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    aria-invalid={!!formErrors.amount}
                    aria-describedby={formErrors.amount ? 'expense-amount-error' : undefined}
                  />
                  {formErrors.amount && (
                    <span id="expense-amount-error" className="field-error" role="alert">
                      {formErrors.amount}
                    </span>
                  )}
                </div>
                <div className={`form-group ${formErrors.category ? 'has-error' : ''}`}>
                  <label htmlFor="expense-category">{t('common.category')}</label>
                  <select
                    id="expense-category"
                    name="category"
                    className={formErrors.category ? 'input-error' : ''}
                    value={formData.category}
                    onChange={handleInputChange}
                    aria-invalid={!!formErrors.category}
                    aria-describedby={formErrors.category ? 'expense-category-error' : undefined}
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="Supplies">{t('expenses.categorySupplies')}</option>
                    <option value="Rent">{t('expenses.categoryRent')}</option>
                    <option value="Utilities">{t('expenses.categoryUtilities')}</option>
                    <option value="Salaries">{t('expenses.categorySalaries')}</option>
                    <option value="Maintenance">{t('expenses.categoryMaintenance')}</option>
                    <option value="Other">{t('expenses.categoryOther')}</option>
                  </select>
                  {formErrors.category && (
                    <span id="expense-category-error" className="field-error" role="alert">
                      {formErrors.category}
                    </span>
                  )}
                </div>
              </div>

              <div className={`form-group ${formErrors.date ? 'has-error' : ''}`}>
                <label htmlFor="expense-date">{t('common.date')}</label>
                <input
                  id="expense-date"
                  type="date"
                  name="date"
                  className={formErrors.date ? 'input-error' : ''}
                  value={formData.date}
                  onChange={handleInputChange}
                  aria-invalid={!!formErrors.date}
                  aria-describedby={formErrors.date ? 'expense-date-error' : undefined}
                />
                {formErrors.date && (
                  <span id="expense-date-error" className="field-error" role="alert">
                    {formErrors.date}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="expense-related-item">{t('expenses.relatedItemLabel')}</label>
                <select
                  id="expense-related-item"
                  name="relatedInventoryId"
                  value={formData.relatedInventoryId}
                  onChange={handleInputChange}
                >
                  <option value="">{t('common.none')}</option>
                  {inventoryItems.map((item) => (
                    <option key={item.itemId} value={item.itemId}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <small>{t('expenses.relatedItemHint')}</small>
              </div>

              <div className="form-group checkbox-group">
                <label htmlFor="expense-is-recurring">
                  <input
                    id="expense-is-recurring"
                    type="checkbox"
                    name="isRecurring"
                    checked={formData.isRecurring}
                    onChange={handleInputChange}
                  />
                  {t('expenses.isRecurring')}
                </label>
              </div>

              {formData.isRecurring && (
                <div className="form-group">
                  <label htmlFor="expense-frequency">{t('expenses.frequency')}</label>
                  <select
                    id="expense-frequency"
                    name="frequency"
                    value={formData.frequency}
                    onChange={handleInputChange}
                  >
                    <option value="WEEKLY">{t('expenses.frequencyWeekly')}</option>
                    <option value="MONTHLY">{t('expenses.frequencyMonthly')}</option>
                    <option value="YEARLY">{t('expenses.frequencyYearly')}</option>
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setModalOpen(false);
                    setFormErrors({});
                  }}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpensesPage;
